/**
 * Image generation (spec 027): the pure parts both sides share. The look
 * presets and platform rules, the unit table, the anchor rule and the
 * request's layers, plus the shapes the image routes answer with. No I/O:
 * the Service (`Service/src/images/`) loads the images and calls the
 * provider; the Editor reads the same constants for its tooltips.
 */
import type { CharacterFraming, PictureKind, PictureMap, PictureSlot } from '../@types';
import {
  DEFAULT_FRAMING, isCharacterFraming, isSlotFilled, masterSlotOf, pictureSlotLabel, pictureSlug, dialogsInPlayOrder, DEFAULT_BACKGROUND_NAME,
  type PictureScene,
} from '../pictures/pictureMap';

/** A kind drawn cut out on a transparent canvas (D-24): characters and props. A full picture (`other`) and a background are opaque (D-36). */
export const isCutOutKind = (kind: PictureKind): boolean => kind === 'character' || kind === 'prop';
/** A kind whose states are edits of a master picture (D-35, D-37): characters and props. */
export const isAnchoredKind = (kind: PictureKind): boolean => kind === 'character' || kind === 'prop';

// ── Owners (spec 030 D-9) ────────────────────────────────────────────────────

/** Who a look, a job or a brief belongs to: a story, or a standalone scene (spec 030). */
export interface ImageOwnerRef { kind: 'story' | 'scene'; id: number }

/** The word a surface names its owner with (030 D-13). */
export const ownerNoun = (owner: Pick<ImageOwnerRef, 'kind'> | null | undefined): 'story' | 'scene' =>
  (owner?.kind === 'scene' ? 'scene' : 'story');

// ── Units (D-3, D-14) ────────────────────────────────────────────────────────

/** The kinds of request this spec sends (D-3). */
export type ImageUsageKind = 'draft' | 'redraw' | 'lookText' | 'briefText';

/** The cost of each kind of provider request in units; the monthly limit is in the same units. */
export const IMAGE_UNITS: Readonly<Record<ImageUsageKind, number>> = Object.freeze({ draft: 1, redraw: 10, lookText: 1, briefText: 1 });

/** Drafts per job unless asked otherwise (1–4). */
export const DEFAULT_DRAFT_COUNT = 4;
export const MAX_DRAFT_COUNT = 4;
/** `IMAGE_MONTHLY_LIMIT` when unset (D-2). */
export const DEFAULT_MONTHLY_LIMIT = 500;

/** Units a job request needs: each slot's drafts plus the redraw of the one to be accepted. */
export const jobUnitsNeeded = (slots: number, count: number = DEFAULT_DRAFT_COUNT): number =>
  slots * (count * IMAGE_UNITS.draft + IMAGE_UNITS.redraw);

/** Units an own-style round needs (D-16): the look text (with a wish) plus one sample. */
export const lookSampleUnitsNeeded = (withWish: boolean): number =>
  (withWish ? IMAGE_UNITS.lookText : 0) + IMAGE_UNITS.draft;

// ── Sizes ────────────────────────────────────────────────────────────────────

/** The provider sizes this spec asks for (gpt-image-1 and its successors share them). */
export type ImageSize = '1024x1024' | '1536x1024' | '1024x1536';
export type ImageQuality = 'low' | 'high';

/**
 * The canvas a picture is drawn on (D-5, D-28, D-36): `square` for Head and
 * shoulders, Half body and props, `portrait` (2:3) for Knees up and Full
 * body, `wide` for backgrounds; a full picture's follows its sprite's box.
 */
export type ImageCanvas = 'square' | 'portrait' | 'wide';

/** Taller (or wider) than this ratio is a portrait (or wide) picture; the midpoint between 1 : 1 and 2 : 3. */
export const PORTRAIT_ASPECT = 1.25;

/** A sprite's box, as `Sprite.size` holds it. */
export interface PictureBox { width: number; height: number }

/**
 * The canvas of a full picture's box (D-36): `wide` when it is more than 1.25
 * times as wide as tall, `portrait` when more than 1.25 times as tall as wide,
 * else (and without a box) `square`.
 */
export const canvasOfBox = (box?: PictureBox | null): ImageCanvas => {
  const w = Number(box?.width), h = Number(box?.height);
  if (!(w > 0) || !(h > 0)) return 'square';
  if (w / h > PORTRAIT_ASPECT) return 'wide';
  if (h / w > PORTRAIT_ASPECT) return 'portrait';
  return 'square';
};

/** The box of a subject's first sprite, in scene order then sprite order (D-36); absent size: 100 × 100; no sprite: undefined. */
export function pictureBoxOf(scenes: readonly PictureScene[], subject: string): PictureBox | undefined {
  const key = pictureSlug(subject);
  if (!key) return undefined;
  for (const scene of scenes) {
    const sprite = (scene.doc.sprites ?? []).find((sp) => pictureSlug(sp.name) === key);
    if (sprite) return { width: sprite.size?.width ?? 100, height: sprite.size?.height ?? 100 };
  }
  return undefined;
}

/** The canvas of a slot: a character's follows its framing (D-28), a full picture's its sprite's box (D-36). */
export const canvasOf = (kind: PictureKind, framing?: CharacterFraming, box?: PictureBox | null): ImageCanvas => {
  if (kind === 'background') return 'wide';
  if (kind === 'other') return canvasOfBox(box);
  if (kind === 'character' && (framing === 'knees' || framing === 'full')) return 'portrait';
  return 'square';
};

/** The canvas of an existing picture of a character (its anchor): portrait when clearly taller than wide, else square. */
export const canvasOfPicture = (width: number, height: number): ImageCanvas =>
  width > 0 && height / width > PORTRAIT_ASPECT ? 'portrait' : 'square';

const PROVIDER_SIZE: Readonly<Record<ImageCanvas, ImageSize>> = Object.freeze({
  square: '1024x1024', portrait: '1024x1536', wide: '1536x1024',
});
const TARGET_SIZE: Readonly<Record<ImageCanvas, { width: number; height: number }>> = Object.freeze({
  square: { width: 1024, height: 1024 }, portrait: { width: 1024, height: 1536 }, wide: { width: 1536, height: 864 },
});

/** The provider's native size for a canvas (D-5). */
export const providerSizeOf = (canvas: ImageCanvas): ImageSize => PROVIDER_SIZE[canvas];

/** The canvas a provider size was asked for. */
export const canvasOfSize = (size: ImageSize): ImageCanvas =>
  (Object.keys(PROVIDER_SIZE) as ImageCanvas[]).find((c) => PROVIDER_SIZE[c] === size) ?? 'square';

/** The library picture's size: square 1024 × 1024, portrait 1024 × 1536, backgrounds 1536 × 864. */
export const slotTargetSize = (canvas: ImageCanvas): { width: number; height: number } => ({ ...TARGET_SIZE[canvas] });

/** A draft's display size: the target downscaled to 256 px on its long side (256 × 256, 171 × 256, 256 × 144). */
export const DRAFT_LONG_SIDE = 256;
export const draftSizeOf = (canvas: ImageCanvas): { width: number; height: number } => {
  const t = slotTargetSize(canvas);
  const scale = DRAFT_LONG_SIDE / Math.max(t.width, t.height);
  return { width: Math.round(t.width * scale), height: Math.round(t.height * scale) };
};

/** A look sample's side: own-style samples and the gallery's WebPs are 512 × 512 (D-18). */
export const LOOK_SAMPLE_SIDE = 512;

// ── The look ─────────────────────────────────────────────────────────────────

export type LookPresetId = 'corporate' | 'flat' | 'ink' | 'watercolour' | 'sketch'
  | 'cutout' | 'clay' | 'comic' | 'anime' | 'chibi' | 'painterly' | 'pixel';
/** `own`: the text came from "Your own style" (D-6). */
export type LookOrigin = LookPresetId | 'own';

/** A story's one look (D-27): edited in place, no versions. */
export interface Look {
  /** The story's look row. */
  id: number;
  /** A preset's id when its text is unchanged, `own` for "Your own style", absent when a preset was edited. */
  presetId?: LookOrigin;
  /** The style description, 1–2000 chars. */
  text: string;
  /** Up to 6 '#rrggbb'. */
  palette?: string[];
}

export const LOOK_TEXT_MAX = 2000;
export const LOOK_PALETTE_MAX = 6;
/** An own-style wish or refinement, 1–500 chars. */
export const LOOK_WISH_MAX = 500;

export interface LookPreset {
  id: LookPresetId;
  name: string;
  subtitle: string;
  text: string;
}

/** The 12 ready-made styles (D-17), in gallery order. */
export const LOOK_PRESETS: readonly LookPreset[] = [
  {
    id: 'corporate',
    name: 'Corporate illustration',
    subtitle: 'friendly people, clean lines',
    text: 'Modern corporate illustration: friendly stylised people with simple rounded features, thin clean outlines, flat fills with subtle gradients, a calm palette of blues, teals and warm accents, plenty of white space, professional and approachable.',
  },
  {
    id: 'flat',
    name: 'Flat vector',
    subtitle: 'bold shapes, no outline',
    text: 'Flat vector illustration: bold, simple shapes with no outlines, solid fills, soft two-tone shading, a limited warm palette, clean modern corporate style.',
  },
  {
    id: 'ink',
    name: 'Ink and wash',
    subtitle: 'outlined, soft colour',
    text: 'Ink and wash illustration: confident dark ink outlines, soft watercolour washes, muted natural colours, visible paper texture, hand-drawn feel.',
  },
  {
    id: 'watercolour',
    name: 'Watercolour',
    subtitle: 'soft edges, warm light',
    text: 'Watercolour painting: soft wet-on-wet washes with gentle bleeding edges, light pencil underdrawing, warm natural light, a fresh airy palette, white paper showing through the highlights.',
  },
  {
    id: 'sketch',
    name: 'Pencil sketch',
    subtitle: 'graphite, hand-drawn',
    text: 'Pencil sketch: graphite line drawing with loose confident strokes, cross-hatched shading, a few light grey tones, no colour except an occasional muted accent, sketchbook paper texture.',
  },
  {
    id: 'cutout',
    name: 'Paper cut-out',
    subtitle: 'layered paper, soft shadows',
    text: 'Paper cut-out illustration: shapes cut from matte coloured card, layered with soft drop shadows between layers, slightly rough cut edges, subtle paper grain, a cheerful limited palette.',
  },
  {
    id: 'clay',
    name: '3D clay',
    subtitle: 'soft 3D, rounded',
    text: 'Soft 3D clay render: rounded chunky forms that look modelled in plasticine, gentle fingerprint texture, soft studio lighting with ambient occlusion, pastel colours, playful and friendly.',
  },
  {
    id: 'comic',
    name: 'Comic book',
    subtitle: 'bold ink, halftone',
    text: 'Comic book art: bold black ink outlines with varied line weight, flat saturated colours, halftone dot shading, strong contrast and dynamic lighting, classic print comic feel.',
  },
  {
    id: 'anime',
    name: 'Anime cel',
    subtitle: 'clean lines, cel shading',
    text: 'Anime cel style: clean even line art, two-tone cel shading with crisp shadow shapes, natural proportions, expressive eyes, bright clear colours, a softly painted background.',
  },
  {
    id: 'chibi',
    name: 'Anime chibi',
    subtitle: 'big head, visual novel',
    text: 'Anime chibi style: big heads and small bodies, large expressive eyes, clean line art, cel shading, bright colours, visual novel character art.',
  },
  {
    id: 'painterly',
    name: 'Painterly',
    subtitle: 'rich brushwork, storybook',
    text: 'Painterly digital illustration: visible confident brushstrokes, rich layered colour, soft edges, warm directional light, a storybook atmosphere with depth and texture.',
  },
  {
    id: 'pixel',
    name: 'Pixel art',
    subtitle: 'retro game, crisp pixels',
    text: 'Pixel art: crisp square pixels at a low resolution, a limited retro palette, no anti-aliasing, simple dithered shading, clear readable silhouettes, classic 16-bit game style.',
  },
];

export const LOOK_PRESET_IDS: readonly LookPresetId[] = LOOK_PRESETS.map((p) => p.id);

export const isLookPresetId = (id: unknown): id is LookPresetId =>
  typeof id === 'string' && (LOOK_PRESET_IDS as readonly string[]).includes(id);

/** A stored preset id as it reads: a preset, `own`, or absent (an edited preset). */
export const lookOriginOf = (id: unknown): LookOrigin | undefined =>
  isLookPresetId(id) || id === 'own' ? (id as LookOrigin) : undefined;

export const lookPreset = (id: string | undefined): LookPreset | undefined => LOOK_PRESETS.find((p) => p.id === id);

/** The look text a story without any look starts from: the Flat vector text. */
export const FALLBACK_LOOK_TEXT = LOOK_PRESETS.find((p) => p.id === 'flat')!.text;

/**
 * The per-kind rules "Copy as style guide" adds in a separate "Picture rules"
 * section (D-25); the Look row and the picker show none of them.
 */
export const ARTIST_RULE_LINES: readonly string[] = [
  'Characters and props: transparent, no background, the whole figure inside the picture with about a tenth of its height clear above the head; framing per character (head and shoulders or half body on a square, knees up or full body on a 2:3 portrait).',
  'Backgrounds: 16:9, no people, eye level.',
  'Full pictures: drawn as described, with their own background, in the shape of the sprite.',
];

/** The look as a plain-text style guide for an artist ("Copy as style guide"): the style, then the picture rules. */
export function styleGuideText(look: Pick<Look, 'text' | 'palette'>): string {
  const lines = ['Look', '', look.text];
  if (look.palette && look.palette.length > 0) lines.push('', `Palette: ${look.palette.join(', ')}`);
  lines.push('', 'Picture rules', ...ARTIST_RULE_LINES);
  return lines.join('\n');
}

/**
 * The fixed instructions the provider's text model writes an own-style look
 * text under (D-15); the author's wish (and the current text, when revising)
 * follow as the input.
 */
export const LOOK_WRITER_INSTRUCTIONS =
  'You write the style description an image model follows. From the author\'s wish, write one paragraph of at most 600 characters '
  + 'describing a visual style: medium, line work, shading, colour, proportions and mood. When a current description is given, revise it '
  + 'to follow the wish and keep what the wish does not change. Name no subject, character, brand or living artist, and ask for no text in '
  + 'the picture. Reply with the paragraph only.';

/** The longest look text a writer reply is cut to. */
export const writtenLookText = (reply: string): string => reply.trim().slice(0, LOOK_TEXT_MAX);

// ── Platform rules and the request ───────────────────────────────────────────

/** Where the head sits (D-22, D-28): every framing rule names it, so the model leaves the room padding cannot restore. */
export const HEAD_PLACEMENT = 'The top of the head sits about one tenth of the image height below the top edge';
const partial = (what: string, cut: string) =>
  `Single character, ${what}, facing the viewer, centred. ${HEAD_PLACEMENT}; the whole figure is inside the frame, with nothing cut at the top or the sides; the figure fills about 80% of the image height and the bottom edge cuts across the ${cut}. No text, no frame.`;

/** Fixed text every request carries last, after the brief (not editable; D-22, D-24, D-28). No rule says "plain". */
export const PLATFORM_RULES = Object.freeze({
  character: Object.freeze({
    head: partial('head and shoulders only, cropped at the chest', 'chest'),
    half: partial('half body from the head to the waist', 'waist'),
    knees: partial('from the head to the knees', 'knees'),
    full: `Single character, full body, feet visible, facing the viewer, centred. ${HEAD_PLACEMENT}; the whole figure from head to feet is inside the frame, with nothing cut at the top, the sides or the bottom; the figure fills about 80% of the image height, with clear space below the feet. No text, no frame.`,
  }) as Readonly<Record<CharacterFraming, string>>,
  characterEdit: 'Same person, same clothes, same framing and size as the first image; change only what the brief describes. No text, no frame.',
  background: 'A place with no people, eye level, 16:9, no text, no frame.',
  /** A prop (D-37): the rule `other` had before 026 v13. */
  prop: 'Single object, centred, with clear empty space around it, no text, no frame.',
  /** A prop's state drawn as an edit of its master (D-37). */
  propEdit: 'Same object, same size, angle and place as the first image; change only what the brief describes. No text, no frame.',
  /** A full picture (D-36): drawn as briefed, nothing else. */
  other: 'No text, no frame.',
  transparent: 'Transparent background, no background at all: draw only the subject and ignore any setting, background, framing or position the brief mentions.',
  sample: 'One character, half body, in front of a small corner of their surroundings, filling a square frame, no text, no frame.',
});

/** The share of the side post-processing keeps clear around a figure (D-22): 61 px at 1024. */
export const FRAME_MARGIN = 0.06;

/** The framing a provenance rules layer was drawn with, when it names one (the redraw reads it). */
export function framingOfRules(rules: string): CharacterFraming | undefined {
  return (Object.keys(PLATFORM_RULES.character) as CharacterFraming[]).find((f) => rules.includes(PLATFORM_RULES.character[f]));
}

// ── Subject-only briefs (D-24) ───────────────────────────────────────────────

const FRAMING_PHRASE = '(?:head and shoulders|half[- ]body|upper[- ]body|full[- ]body|full[- ]length|knees[- ]up|waist[- ]up|bust)(?: (?:shot|portrait|view|framing|crop))?';
const PLAIN_BACKGROUND_PHRASE = '(?:(?:on|against|in front of|over) )?(?:(?:a|an) )?(?:plain|blank|empty|neutral|simple|solid|white|transparent)(?: (?:white|grey|gray|light|pale|dark|black|colou?red))? background';
const POSITION_PHRASES = [
  '(?:standing|positioned|placed|shown) (?:at|on|to) the (?:left|right|centre|center)(?: of the (?:frame|picture|image))?',
  '(?:at|on|to) the (?:left|right) of the (?:frame|picture|image)',
  'cent(?:red|ered) in the (?:frame|picture|image)',
];
/** The fixed list `subjectOnlyBrief` drops (D-24), each with the comma, "and" or space before it. */
export const BRIEF_STRIP_PATTERNS: readonly RegExp[] = [FRAMING_PHRASE, PLAIN_BACKGROUND_PHRASE, ...POSITION_PHRASES]
  .map((p) => new RegExp(`(?:\\s*,\\s*|\\s+and\\s+|\\s+|^)${p}\\b`, 'gi'));

/**
 * A character's or prop's brief without framing, plain-background or
 * position phrases (D-24); every other word is kept. Briefs written before
 * v9 (story 341's) say "half body on a plain background, standing at the
 * left"; the rules decide those things.
 */
export function subjectOnlyBrief(brief: string): string {
  let out = brief;
  for (const re of BRIEF_STRIP_PATTERNS) out = out.replace(re, '');
  return out
    .replace(/\s+([,.;:!?])/g, '$1')
    .replace(/([,;])(?:\s*[,;])+/g, '$1')
    .replace(/[,;]\s*\./g, '.')
    .replace(/^[\s,;]+|[\s,;]+$/g, '')
    .replace(/\s{2,}/g, ' ')
    .replace(/^([a-z])/, (_m, c: string) => c.toUpperCase())
    .trim();
}

/** The fixed subject of every look sample (D-18), so the author compares like with like. */
export const LOOK_SAMPLE_BRIEF =
  'A friendly office worker in her thirties with shoulder-length dark hair, a teal cardigan over a white shirt, holding a coffee mug and '
  + 'smiling slightly, standing by a desk with a laptop, a potted plant and a window behind her.';

/**
 * The anchor of a character or prop slot (027 D-35, D-37): the master picture only
 * (`masterSlotOf`). The master itself is never anchored: it is drawn from its
 * brief and the character's framing alone, whatever its expressions show.
 * Every other expression is an edit of the master's current picture when the
 * master is filled, and is drawn from its brief alone while the master is
 * empty, never from another expression. `exceptSlotId` is the slot being
 * generated; without it the result is the master's picture, if any.
 */
export function anchorFor(map: PictureMap | null | undefined, subject: string, exceptSlotId?: string): number | undefined {
  const master = masterSlotOf(map, subject);
  if (!master || master.id === exceptSlotId || !isSlotFilled(master)) return undefined;
  return master.mediaId;
}

/** What provenance keeps of a request's layers. */
export interface ImageLayers {
  rules: string;
  look: string;
  brief: string;
}

/**
 * The look layer a request sends for `look` (the trimmed text; the palette
 * is not in it). A generated picture is in the previous look when its
 * provenance's `layers.look` differs from this for the story's look now
 * (D-26).
 */
export const lookLayerOf = (look: Pick<Look, 'text'>): string => (look.text ?? '').trim();

/** A request with a media id where the Service will put image bytes. */
export interface AssembledImageRequest {
  prompt: string;
  /** The character's anchor (expressions); the only image a request carries (D-7). */
  editOf?: number;
  /** The canvas's provider size (D-28); `canvasOfSize` gives post-processing's target back. */
  size: ImageSize;
  quality: ImageQuality;
  /** Characters and props (D-24); never a background or a full picture (D-36). */
  transparent: boolean;
}

export interface AssembleInput {
  slot: Pick<PictureSlot, 'kind' | 'brief'> & { framing?: CharacterFraming };
  look: Pick<Look, 'text'>;
  /** `anchorFor(...)` of the slot's subject; used for characters and props only (D-35, D-37). */
  anchor?: number;
  /** A full picture's sprite box (`pictureBoxOf`, D-36): its canvas follows it. */
  box?: PictureBox;
  /**
   * The anchor picture's canvas (`canvasOfPicture` of its size, D-28): an
   * expression is drawn on its anchor's canvas, and its framing's rule is
   * added only when that canvas is the framing's. Absent: the framing's canvas.
   */
  anchorCanvas?: ImageCanvas;
  /** The edited brief sent with the request; else the slot's. */
  brief?: string;
  quality: ImageQuality;
}

const joinLayers = (...texts: string[]): string => texts.filter((t) => t.length > 0).join('\n\n');

/** The prompt of a request's layers: the look, the brief, then the rules (they come last and win, D-24). */
export const promptOfLayers = (layers: ImageLayers): string =>
  joinLayers((layers.look ?? '').trim(), (layers.brief ?? '').trim(), (layers.rules ?? '').trim());

/**
 * The platform rules of a slot (D-22, D-24, D-28). An anchored expression
 * gets the edit rule and, when `framing` is given, that framing's rule too
 * (the jobs route passes it only when the anchor's canvas is the framing's).
 */
export function rulesFor(kind: PictureKind, opts: { framing?: CharacterFraming; anchored?: boolean } = {}): string {
  if (kind === 'background') return PLATFORM_RULES.background;
  if (kind === 'other') return PLATFORM_RULES.other;
  if (kind === 'prop') {
    return opts.anchored
      ? `${PLATFORM_RULES.propEdit} ${PLATFORM_RULES.prop} ${PLATFORM_RULES.transparent}`
      : `${PLATFORM_RULES.prop} ${PLATFORM_RULES.transparent}`;
  }
  if (opts.anchored) {
    return isCharacterFraming(opts.framing)
      ? `${PLATFORM_RULES.characterEdit} ${PLATFORM_RULES.character[opts.framing]} ${PLATFORM_RULES.transparent}`
      : `${PLATFORM_RULES.characterEdit} ${PLATFORM_RULES.transparent}`;
  }
  const framing = isCharacterFraming(opts.framing) ? opts.framing : DEFAULT_FRAMING;
  return `${PLATFORM_RULES.character[framing]} ${PLATFORM_RULES.transparent}`;
}

/**
 * The layers in order: the anchor image (as `editOf`, characters and props
 * only), the look's text, the brief (subject only for characters and props),
 * the platform rules last. The prompt is the three text layers joined by
 * blank lines.
 */
export function assembleRequest({ slot, look, anchor, anchorCanvas, box, brief, quality }: AssembleInput): {
  request: AssembledImageRequest;
  layers: ImageLayers;
} {
  const editOf = isAnchoredKind(slot.kind) && typeof anchor === 'number' && anchor > 0 ? anchor : undefined;
  const framing = slot.kind === 'character' ? (isCharacterFraming(slot.framing) ? slot.framing : DEFAULT_FRAMING) : undefined;
  const own = canvasOf(slot.kind, framing, box);
  const canvas = editOf !== undefined && anchorCanvas ? anchorCanvas : own;
  const rules = rulesFor(slot.kind, {
    framing: editOf !== undefined && canvas !== own ? undefined : framing,
    anchored: editOf !== undefined,
  });
  const raw = (brief ?? slot.brief ?? '').trim();
  const theBrief = isCutOutKind(slot.kind) ? subjectOnlyBrief(raw) : raw;
  const layers = { rules, look: lookLayerOf(look), brief: theBrief };
  return {
    request: {
      prompt: promptOfLayers(layers),
      ...(editOf !== undefined ? { editOf } : {}),
      size: providerSizeOf(canvas),
      quality,
      transparent: isCutOutKind(slot.kind),
    },
    layers,
  };
}

// ── Cut-off drafts (D-29) ────────────────────────────────────────────────────

/** An edge of the picture a figure runs into. */
export type CutEdge = 'top' | 'sides' | 'bottom';

/**
 * A figure is cut at an edge when an opaque run along that edge's outer
 * `CUT_EDGE_DEPTH` pixels is at least `CUT_RUN_SHARE` of the edge's length
 * (41 px along a 1024 edge): a strand of hair touching the edge is not a cut,
 * a head flattened by it is.
 */
export const CUT_RUN_SHARE = 0.04;
export const CUT_EDGE_DEPTH = 2;
/** Alpha above this is opaque when measuring a cut. */
export const CUT_ALPHA = 128;
/** For the partial framings, the sides are measured above this share of the height (the bottom cut may spill into the corners). */
export const CUT_SIDE_SPAN_PARTIAL = 0.9;

/** The tile marker of a cut draft (D-29), in edge order. */
export const CUT_LABELS: Readonly<Record<CutEdge, string>> = Object.freeze({
  top: 'Head cut off', sides: 'Cut off at the side', bottom: 'Feet cut off',
});
export const cutLabel = (edges: readonly CutEdge[]): string =>
  (['top', 'sides', 'bottom'] as CutEdge[]).filter((e) => edges.includes(e)).map((e) => CUT_LABELS[e]).join(' · ');

/** The edges a figure is cut at, from its RGBA pixels (D-29). The bottom counts only for `full`. */
export function cutEdgesOf(
  r: { data: Uint8Array; width: number; height: number },
  framing?: CharacterFraming,
): CutEdge[] {
  const { data, width: W, height: H } = r;
  const opaque = (x: number, y: number) => data[(y * W + x) * 4 + 3] > CUT_ALPHA;
  const rowRun = (y: number) => {
    let best = 0, run = 0;
    for (let x = 0; x < W; x++) { run = opaque(x, y) ? run + 1 : 0; if (run > best) best = run; }
    return best;
  };
  const colRun = (x: number, until: number) => {
    let best = 0, run = 0;
    for (let y = 0; y < until; y++) { run = opaque(x, y) ? run + 1 : 0; if (run > best) best = run; }
    return best;
  };
  const depth = Math.min(CUT_EDGE_DEPTH, W, H);
  const out: CutEdge[] = [];
  const rows = (ys: number[]) => Math.max(0, ...ys.map(rowRun));
  if (rows([...Array(depth).keys()]) >= CUT_RUN_SHARE * W) out.push('top');
  const span = framing === 'full' ? H : Math.round(H * CUT_SIDE_SPAN_PARTIAL);
  const cols = [...Array(depth).keys()].flatMap((i) => [i, W - 1 - i]);
  if (Math.max(0, ...cols.map((x) => colRun(x, span))) >= CUT_RUN_SHARE * H) out.push('sides');
  if (framing === 'full' && rows([...Array(depth).keys()].map((i) => H - 1 - i)) >= CUT_RUN_SHARE * W) out.push('bottom');
  return out;
}

// ── Briefs written from the story (D-23) ─────────────────────────────────────

/** What "Write from the story" tells the provider's text model about a slot. */
export interface BriefContext {
  kind: PictureKind;
  /** The sprite's or background's name as the story has it ("Guest (Jordan)"). */
  subject: string;
  variant: string;
  /** The story's title, or a scene owner's scene name (spec 030). */
  storyTitle: string;
  /** Whose picture it is (spec 030): absent for a story; a scene's input says "Scene:". */
  owner?: 'story' | 'scene';
  /** At most 12, each at most 200 characters, in story order. */
  lines: string[];
  /** At most 6 scene names and named backgrounds, distinct, in story order. */
  places: string[];
}

export const BRIEF_CONTEXT_LINES = 12;
export const BRIEF_CONTEXT_LINE_MAX = 200;
export const BRIEF_CONTEXT_PLACES = 6;
/** 026's brief limit. */
export const BRIEF_MAX = 500;

export const BRIEF_WRITER_INSTRUCTIONS =
  'You write the brief an image model draws one picture from. Write one or two sentences, at most 400 characters. '
  + 'For a character, describe only who the character is (apparent age, role, build, hair, clothing and one telling detail that fits the story) '
  + 'and the named expression; the name may read "Role (Name)". Never mention a framing, a crop, a position, a pose in the frame, a setting '
  + 'or any background for a character or a prop: the app sets those. '
  + 'For a prop, describe only the object (what it is, its material, its colours and one telling detail) and how it looks in the named state. '
  + 'For a background, describe the place, with no people. '
  + 'For a full picture, describe the whole picture, setting and light included, as it should fill the frame. '
  + 'Ask for no text in the picture. Reply with the brief only.';

/** The text model's input: the context as labelled lines. */
export function briefWriterInput(c: BriefContext): string {
  const what = c.kind === 'background' ? `Background: ${c.subject}${c.variant ? `, state: ${c.variant}` : ''}`
    : c.kind === 'prop' ? `Object: ${c.subject}${c.variant ? `, state: ${c.variant}` : ''}`
      : c.kind === 'other' ? `Full picture: ${c.subject}${c.variant ? `, ${c.variant}` : ''}`
        : `Character: ${c.subject}${c.variant ? `, expression: ${c.variant}` : ''}`;
  const out = [`Picture: ${what}`, `${c.owner === 'scene' ? 'Scene' : 'Story'}: ${c.storyTitle}`];
  if (c.lines.length > 0) out.push('Lines:', ...c.lines.map((l) => `- ${l}`));
  if (c.places.length > 0) out.push(`Places: ${c.places.join('; ')}`);
  return out.join('\n');
}

/** A writer reply as a brief: trimmed, subject only for characters and props (D-24), at most 500 characters. */
export function writtenBrief(reply: string, kind: PictureKind): string {
  const t = reply.trim().replace(/^["“]|["”]$/g, '').trim();
  return (isCutOutKind(kind) ? subjectOnlyBrief(t) : t).slice(0, BRIEF_MAX).trim();
}

const cut = (t: string, n: number) => (t.length > n ? `${t.slice(0, n - 1).trimEnd()}…` : t);

/** The context of a slot (D-23), from the map and the story's scenes; undefined for a slot not in the map. */
export function briefContextOf(
  map: PictureMap | null | undefined,
  scenes: readonly PictureScene[],
  storyTitle: string,
  slotId: string,
  owner: 'story' | 'scene' = 'story',
): BriefContext | undefined {
  const slot = map?.slots.find((s) => s.id === slotId);
  if (!slot) return undefined;
  const usedIn = new Set(slot.uses.map((u) => u.scene));
  const lines: string[] = [];
  const places: string[] = [];
  const addPlace = (p: string) => {
    const t = p.trim();
    if (t && t !== DEFAULT_BACKGROUND_NAME && !places.includes(t) && places.length < BRIEF_CONTEXT_PLACES) places.push(t);
  };
  for (const scene of scenes) {
    if (!usedIn.has(scene.key)) continue;
    addPlace(scene.doc.name || scene.name);
    addPlace(scene.doc.background?.name ?? '');
    const refs = dialogsInPlayOrder(scene.doc.dialogs ?? []).flatMap((d) => (d.lines ?? []).map((line) => ({ line })));
    if (slot.kind === 'background') {
      for (const r of refs.slice(0, 3)) {
        const t = (r.line.text ?? '').trim();
        if (t && lines.length < BRIEF_CONTEXT_LINES) lines.push(cut(t, BRIEF_CONTEXT_LINE_MAX));
      }
      continue;
    }
    const mine = new Set((scene.doc.sprites ?? []).filter((sp) => pictureSlug(sp.name) === pictureSlug(slot.subject)).map((sp) => sp.id));
    for (const r of refs) {
      const t = (r.line.text ?? '').trim();
      if (!t || !r.line.expression || !mine.has(r.line.expression.spriteId)) continue;
      if (lines.length < BRIEF_CONTEXT_LINES) lines.push(cut(t, BRIEF_CONTEXT_LINE_MAX));
    }
  }
  return {
    kind: slot.kind, subject: slot.subject, variant: slot.variant, storyTitle: storyTitle.trim(),
    ...(owner === 'scene' ? { owner } : {}), lines, places,
  };
}

/** What the fake writer answers (027's fake provider). */
export const fakeBriefText = (c: Pick<BriefContext, 'kind' | 'subject' | 'variant' | 'storyTitle'>): string =>
  `Fake brief: ${c.kind === 'background' && !c.variant ? c.subject : pictureSlotLabel(c)}, from "${c.storyTitle}".`;

/** The request for one look sample (gallery and own style alike): no image, the sample rules, the look, the sample brief. */
export function assembleSample(lookText: string): AssembledImageRequest {
  return {
    prompt: joinLayers(PLATFORM_RULES.sample, lookText.trim(), LOOK_SAMPLE_BRIEF),
    size: '1024x1024',
    quality: 'low',
    transparent: false,
  };
}

/** The SHA-256 (hex) of a look sample's assembled prompt, as `samples.json` records it. */
export const lookSamplePromptSha = (lookText: string): string => sha256Hex(assembleSample(lookText).prompt);

/** SHA-256 of a string's UTF-8 bytes, hex; pure and synchronous so both sides and tests share it. */
export function sha256Hex(text: string): string {
  const bytes = new TextEncoder().encode(text);
  const K = new Uint32Array([
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
    0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
    0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
  ]);
  const h = new Uint32Array([0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19]);
  const len = bytes.length;
  const total = Math.ceil((len + 9) / 64) * 64;
  const buf = new Uint8Array(total);
  buf.set(bytes);
  buf[len] = 0x80;
  const view = new DataView(buf.buffer);
  view.setUint32(total - 8, Math.floor((len * 8) / 0x100000000));
  view.setUint32(total - 4, (len * 8) >>> 0);
  const w = new Uint32Array(64);
  const rotr = (x: number, n: number) => (x >>> n) | (x << (32 - n));
  for (let off = 0; off < total; off += 64) {
    for (let i = 0; i < 16; i++) w[i] = view.getUint32(off + i * 4);
    for (let i = 16; i < 64; i++) {
      const s0 = rotr(w[i - 15], 7) ^ rotr(w[i - 15], 18) ^ (w[i - 15] >>> 3);
      const s1 = rotr(w[i - 2], 17) ^ rotr(w[i - 2], 19) ^ (w[i - 2] >>> 10);
      w[i] = (w[i - 16] + s0 + w[i - 7] + s1) >>> 0;
    }
    let [a, b, c, d, e, f, g, hh] = h;
    for (let i = 0; i < 64; i++) {
      const S1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
      const ch = (e & f) ^ (~e & g);
      const t1 = (hh + S1 + ch + K[i] + w[i]) >>> 0;
      const S0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
      const mj = (a & b) ^ (a & c) ^ (b & c);
      const t2 = (S0 + mj) >>> 0;
      hh = g; g = f; f = e; e = (d + t1) >>> 0; d = c; c = b; b = a; a = (t1 + t2) >>> 0;
    }
    h[0] += a; h[1] += b; h[2] += c; h[3] += d; h[4] += e; h[5] += f; h[6] += g; h[7] += hh;
  }
  return Array.from(h, (x) => x.toString(16).padStart(8, '0')).join('');
}

/** The default library name: `<subject>-<variant>.png`, backgrounds `<subject>.png` (a background state `<subject>-<state>.png`, spec 039). */
export function defaultLibraryName(slot: Pick<PictureSlot, 'kind' | 'subject' | 'variant'>): string {
  const subject = pictureSlug(slot.subject) || 'picture';
  const variant = pictureSlug(slot.variant);
  return `${subject}${variant ? `-${variant}` : ''}.png`;
}

// ── What the routes answer with ──────────────────────────────────────────────

export type ImageMode = 'platform' | 'byok' | 'off';

/** `GET /images/settings`. */
export interface ImageSettingsView {
  mode: ImageMode;
  provider: 'openai';
  model: string;
  available: boolean;
  stub?: boolean;
  limit: {
    /** The effective limit in units. */
    monthly: number;
    /** `IMAGE_MONTHLY_LIMIT`. */
    serverMonthly: number;
    /** The admin's own lower limit (D-13). */
    own?: number;
    used: number;
    left: number;
    /** 'YYYY-MM', UTC. */
    month: string;
  };
  byok: { set: boolean; last4?: string };
}

export type DraftStatus = 'queued' | 'running' | 'ready' | 'failed';
export type DraftFailReason = 'rejected_key' | 'rate_limited' | 'refused' | 'failed';
export type ImageJobStatus = 'running' | 'ready' | 'cancelled' | 'interrupted';
export type FinalStatus = 'drawing' | 'ready' | 'failed';

export interface ImageDraftView {
  index: number;
  status: DraftStatus;
  reason?: DraftFailReason;
  /** A ready draft whose figure runs into an edge (D-29); absent otherwise (never `false`, per the JSON quirk). */
  cut?: true;
  /** The edges it is cut at, with `cut`. */
  cutEdges?: CutEdge[];
}

/** `GET /image-jobs/:jobId`. */
export interface ImageJobView {
  status: ImageJobStatus;
  slotId: string;
  brief: string;
  drafts: ImageDraftView[];
  /** The canvas the drafts are drawn on (D-28), so the panel shapes their tiles; the Service always sends it. */
  canvas?: ImageCanvas;
  accepted?: { mediaId: number; final: FinalStatus };
}

/** `GET /stories/:id/look`. */
export interface StoryLookView {
  /** The story's look (D-27). */
  look?: Look;
  /** Without a look of its own: the look of the author's most recently confirmed story, which the picker preselects (D-20). */
  suggested?: Look;
  confirmed: boolean;
  /** Present only when at least one generated picture this story shows is in the previous look (D-26, D-30). */
  oldLook?: OldLookView;
}

/** The story's pictures in the previous look (D-26): the media, each once ascending, and the slots showing them, in map order. */
export interface OldLookView { mediaIds: number[]; slotIds: string[] }

/** `POST /stories/:id/look/sample`. */
export interface LookSampleView { text: string; sampleId: string }

/** `GET /media/:id` `provenance`. */
export interface ImageProvenanceView {
  lookId: number;
  model: string;
  provider: string;
  layers: ImageLayers;
  final: FinalStatus;
}

/** The `429 monthly_limit_reached` details. */
export interface MonthlyLimitDetails { needed: number; left: number; monthly: number }
